import { isLightColor } from './functions';

/**
 * Keep a default theme as a fallback in case
 * something goes wrong.
 */
export const THEME_DEFAULTS = {
  background: '#88869b',

  cardLightBackground: '#f2efeb',
  cardLightText: '#1d1d1b',
  cardLightTitle: '#ba272d',

  cardDarkBackground: '#1d1d1b',
  cardDarkText: '#ffffff',
  cardDarkTitle: '#ffffff',

  cardHighlightBackground: '#ba272d',
  cardHighlightText: '#EAE8E6',
  cardHighlightTitle: '#EAE8E6',

  cardArtworkBackground: '#332644',
  cardArtworkTitle: '#ffffff',
  cardArtworkText: '#ffffff',

  cardMixBackground: '#a9b595',
  cardMixBackground2: '#748d5c',
  cardMixTitle: '#1d1d1b',
  cardMixText: '#1d1d1b',

  cardMixLabelBackground: '#ffffff',
  cardMixLabelText: '#1d1d1b'
};

/**
 * Checks if a color is light or dark and returns the text color with more
 * contrast.
 */
export const textColorForBackground = backgroundColor =>
  isLightColor(backgroundColor)
    ? THEME_DEFAULTS.cardLightText
    : THEME_DEFAULTS.cardDarkText;

/**
 * Construct a theme object by merging the color scheme with the color
 * scheme.
 */
export const buildThemeFromColors = (colors = {}) => {
  const artworkBackground = colors.artworkBackground;
  const artworkText = textColorForBackground(artworkBackground);

  const highlightBackground = colors.highlightBackground;
  const textOnHighlight = textColorForBackground(highlightBackground);
  const highlightText = colors.highlightText;

  const mixBackground = colors.mixBackground;
  const mixBackground2 = colors.mixBackground2;
  const textOnMix = textColorForBackground(mixBackground);

  return {
    background: colors.pageBackground || THEME_DEFAULTS.background,

    cardLightBackground:
      colors.cardLightBackground || THEME_DEFAULTS.cardLightBackground,
    cardLightText: colors.cardLightText || THEME_DEFAULTS.cardLightText,
    cardLightTitle: highlightText || THEME_DEFAULTS.cardLightTitle,

    cardDarkBackground:
      colors.cardDarkBackground || THEME_DEFAULTS.cardDarkBackground,
    cardDarkText: colors.cardDarkText || THEME_DEFAULTS.cardDarkText,
    cardDarkTitle: colors.cardDarkTitle || THEME_DEFAULTS.cardDarkTitle,

    cardHighlightBackground:
      highlightBackground || THEME_DEFAULTS.cardHighlightBackground,
    cardHighlightText: textOnHighlight || THEME_DEFAULTS.cardHighlightText,
    cardHighlightTitle: textOnHighlight || THEME_DEFAULTS.cardHighlightTitle,

    cardArtworkBackground:
      artworkBackground || THEME_DEFAULTS.cardArtworkBackground,
    cardArtworkTitle: artworkText || THEME_DEFAULTS.cardArtworkTitle,
    cardArtworkText: artworkText || THEME_DEFAULTS.cardArtworkText,

    cardMixBackground: mixBackground || THEME_DEFAULTS.cardMixBackground,
    cardMixBackground2: mixBackground2 || THEME_DEFAULTS.cardMixBackground2,
    cardMixText: textOnMix || THEME_DEFAULTS.cardMixText,
    cardMixTitle: textOnMix || THEME_DEFAULTS.cardMixTitle,

    cardMixLabelBackground:
      colors.cardMixLabelBackground || THEME_DEFAULTS.cardMixLabelBackground,
    cardMixLabelText: colors.cardMixLabelText || THEME_DEFAULTS.cardMixLabelText
  };
};

/**
 * Transforms a theme object into a CSS custom property representation.
 */
export const buildThemeStylesFromColors = colors => {
  const theme = buildThemeFromColors(colors);

  return Object.entries({
    '--theme-background': theme.background,

    '--theme-card-default': theme.cardLightBackground,
    '--theme-text-default': theme.cardLightText,
    '--theme-title-default': theme.cardLightTitle,
    '--theme-text-accent': theme.cardLightTitle,

    '--theme-dark-card-default': theme.cardDarkBackground,
    '--theme-dark-text-default': theme.cardDarkText,
    '--theme-dark-title-default': theme.cardDarkTitle,
    '--theme-dark-text-accent': theme.cardDarkTitle,

    '--theme-accent': theme.cardHighlightBackground,
    '--theme-text-on-accent': theme.cardHighlightText,

    '--theme-card-quote': theme.cardHighlightBackground,
    '--theme-text-quote': theme.cardHighlightText,

    '--theme-card-artwork': theme.cardArtworkBackground,
    '--theme-card-artwork-reverse': theme.cardLightBackground,
    '--theme-title-artwork': theme.cardArtworkTitle,
    '--theme-text-artwork': theme.cardArtworkText,

    '--theme-card-mix': theme.cardMixBackground,
    '--theme-card-mix-accent': theme.cardMixBackground2,
    '--theme-text-mix': theme.cardMixText,
    '--theme-title-mix': theme.cardMixTitle,
    '--theme-mix-label-background': theme.cardMixLabelBackground,
    '--theme-mix-label-text': theme.cardMixLabelText,

    '--theme-card-playlist': theme.cardArtworkBackground,
    '--theme-title-playlist': theme.cardArtworkTitle,
    '--theme-text-playlist': theme.cardArtworkText
  })
    .map(([k, v]) => `${k}: ${v};`)
    .join('\n');
};
